import type { SigilKnotPoint } from '@sorrem/shared';

/** A stretch of the knot that stays on one side of every crossing. */
export type SigilRun = readonly SigilKnotPoint[];

/**
 * Splits the closed knot into runs that stay on one side (over or under).
 * Each run overlaps its neighbors by a sample so the strand stays unbroken.
 */
export function splitSigilRuns(points: readonly SigilKnotPoint[]): { over: SigilRun[]; under: SigilRun[] } {
    const isOver = (point: SigilKnotPoint): boolean => point.z >= 0;
    const count = points.length;
    // Start at a side change so no run wraps past the end of the array.
    const start = points.findIndex((point, index) => isOver(point) !== isOver(points[(index + count - 1) % count]!));
    const over: SigilRun[] = [];
    const under: SigilRun[] = [];
    let run: SigilKnotPoint[] = [points[(start + count - 1) % count]!];
    for (let step = 0; step < count; step += 1) {
        const point = points[(start + step) % count]!;
        const next = points[(start + step + 1) % count]!;
        run.push(point);
        if (isOver(next) !== isOver(point)) {
            run.push(next);
            (isOver(point) ? over : under).push(run);
            run = [point];
        }
    }
    return { over, under };
}

/** An SVG path through a run, scaled from the knot's unit circle to `radius`. */
export function sigilRunPath(run: SigilRun, radius: number): string {
    return run.map((point, index) => `${index === 0 ? 'M' : 'L'}${(point.x * radius).toFixed(2)} ${(point.y * radius).toFixed(2)}`).join('');
}

/** A run with `trim` samples cut from each end (keeping at least two), so an over-strand's gap never notches the strand it continues. */
export function trimSigilRun(run: SigilRun, trim: number): SigilRun {
    return run.slice(trim, Math.max(trim + 2, run.length - trim));
}
